import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Plus, Trash2, ShieldCheck, Edit3, Copy, Search } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Pagination, { usePagination } from "@/components/Pagination";

const MODULE_LABELS = {
  dashboard: "Dashboard",
  invoices: "Invoices",
  payments: "Payments",
  retentions: "Retentions",
  ld: "LD Amount",
  jobsheet: "Jobsheet / SES",
  users: "Users",
  roles: "Roles",
  status_options: "Status Options",
  excel_upload: "Excel Upload",
  reports: "Reports",
  projects: "Projects",
  parties: "Parties · TDS",
  audit_log: "Audit Log",
  hr_compliance: "HR Compliance",
  credit_notes: "Credit Notes",
};

const ACTION_LABELS = { view: "View", create: "Create", edit: "Edit", delete: "Delete" };
const EMPTY_PERM = { view: false, create: false, edit: false, delete: false };

export default function Roles() {
  const { hasPerm } = useAuth();
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState(["view", "create", "edit", "delete"]);
  const [heads, setHeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", permissions: {}, retention_heads: [] });
  const [q, setQ] = useState("");
  const [filterKind, setFilterKind] = useState("__all__"); // __all__, system, custom
  const [filterModule, setFilterModule] = useState("__all__"); // __all__ or module key
  const [filterHead, setFilterHead] = useState("__all__"); // __all__ or head name

  const load = async () => {
    const [r, m] = await Promise.all([api.get("/roles"), api.get("/roles/modules")]);
    setRoles(r.data);
    setModules(m.data.modules || []);
    setActions(m.data.actions || ["view", "create", "edit", "delete"]);
    setHeads(m.data.retention_heads || []);
  };

  useEffect(() => { load(); }, []);

  const filteredRoles = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return roles.filter((r) => {
      // Search filter
      if (needle) {
        const rh = Array.isArray(r.retention_heads) ? r.retention_heads.join(" ") : "";
        const enabledModules = Object.entries(r.permissions || {})
          .filter(([, v]) => v && (typeof v === "boolean" ? v : Object.values(v).some(Boolean)))
          .map(([k]) => `${k} ${MODULE_LABELS[k] || ""}`)
          .join(" ");
        const hay = `${r.name || ""} ${r.description || ""} ${rh} ${enabledModules}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      // System vs Custom filter
      if (filterKind === "system" && !r.is_system) return false;
      if (filterKind === "custom" && r.is_system) return false;
      // Module access filter — role must have at least View on this module
      if (filterModule !== "__all__") {
        const perm = r.permissions?.[filterModule];
        const hasAccess = perm && (typeof perm === "boolean" ? perm : !!perm.view);
        if (!hasAccess) return false;
      }
      // Retention head filter — role must manage this head (empty array = all heads)
      if (filterHead !== "__all__") {
        const rh = Array.isArray(r.retention_heads) ? r.retention_heads : [];
        if (rh.length > 0 && !rh.includes(filterHead)) return false;
      }
      return true;
    });
  }, [roles, q, filterKind, filterModule, filterHead]);

  const {
    pageItems: pagedRoles,
    page: rolePage, setPage: setRolePage,
    pageSize: rolePageSize, setPageSize: setRolePageSize,
    totalPages: roleTotalPages, totalItems: roleTotalItems,
  } = usePagination(filteredRoles, "voltledger.roles.pageSize");

  const activeFilterCount = (filterKind !== "__all__" ? 1 : 0) + (filterModule !== "__all__" ? 1 : 0) + (filterHead !== "__all__" ? 1 : 0);
  const clearAllFilters = () => {
    setQ("");
    setFilterKind("__all__");
    setFilterModule("__all__");
    setFilterHead("__all__");
  };

  const normalizePerm = (v) => {
    if (typeof v === "boolean") return { view: v, create: v, edit: v, delete: v };
    if (v && typeof v === "object") return { ...EMPTY_PERM, ...v };
    return { ...EMPTY_PERM };
  };

  const startCreate = () => {
    const perms = Object.fromEntries(modules.map((m) => [m, { ...EMPTY_PERM }]));
    setForm({ name: "", description: "", permissions: perms, retention_heads: [...heads] });
    setEditing(null);
    setOpen(true);
  };

  const startEdit = (role) => {
    const perms = Object.fromEntries(modules.map((m) => [m, normalizePerm(role.permissions?.[m])]));
    const rh = Array.isArray(role.retention_heads) && role.retention_heads.length ? role.retention_heads : [...heads];
    setForm({ name: role.name, description: role.description || "", permissions: perms, retention_heads: rh });
    setEditing(role);
    setOpen(true);
  };

  const togglePerm = (m, a) => {
    setForm((f) => {
      const next = { ...f.permissions };
      const row = { ...(next[m] || EMPTY_PERM) };
      row[a] = !row[a];
      // If turning ON create/edit/delete, also turn ON view (must view to act).
      if (row[a] && a !== "view") row.view = true;
      // If turning OFF view, also turn off all other actions.
      if (!row[a] && a === "view") { row.create = false; row.edit = false; row.delete = false; }
      next[m] = row;
      return { ...f, permissions: next };
    });
  };

  const toggleModule = (m, on) => {
    setForm((f) => ({
      ...f,
      permissions: { ...f.permissions, [m]: on ? { view: true, create: true, edit: true, delete: true } : { ...EMPTY_PERM } },
    }));
  };

  const toggleHead = (h) => {
    setForm((f) => {
      const set = new Set(f.retention_heads);
      if (set.has(h)) set.delete(h); else set.add(h);
      return { ...f, retention_heads: Array.from(set) };
    });
  };

  const submit = async () => {
    try {
      const payload = {
        ...form,
        retention_heads: form.retention_heads.length === heads.length ? [] : form.retention_heads,
      };
      if (editing) {
        await api.put(`/roles/${editing.id}`, payload);
        toast.success("Role updated");
      } else {
        await api.post("/roles", payload);
        toast.success("Role created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this role?")) return;
    try {
      await api.delete(`/roles/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const clone = async (role) => {
    const name = window.prompt(`Clone role "${role.name}" as:`, `${role.name} (Copy)`);
    if (!name || !name.trim()) return;
    try {
      await api.post(`/roles/${role.id}/clone`, { name: name.trim() });
      toast.success(`Role "${name.trim()}" created from "${role.name}"`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clone failed");
    }
  };

  const canManage = hasPerm("roles", "create") || hasPerm("roles", "edit");
  const isSuperAdminEditing = editing && editing.name === "Super Admin";

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1500px]">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-red-600/90">Administration</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">Roles &amp; Permissions</h1>
          <p className="text-sm text-slate-500 mt-2">
            Configure granular access per module (view/create/edit/delete) and per retention head. Super Admin always has full access.
            {(q || activeFilterCount > 0) && <span className="ml-2 text-indigo-600 font-semibold">· {filteredRoles.length} match{filteredRoles.length === 1 ? "" : "es"}</span>}
          </p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="relative w-72">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input
              placeholder="Search role name, description, module, head…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="rounded-none pl-9 pr-9 h-10 text-sm border-slate-300 focus-visible:ring-[#002FA7] focus-visible:border-[#002FA7]"
              data-testid="roles-search"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                className="absolute right-3 top-3 text-slate-400 hover:text-slate-700 text-xs font-semibold"
                data-testid="roles-search-clear"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={startCreate} data-testid="new-role-btn" className="bg-[#002FA7] hover:bg-[#00227A] rounded-none h-10">
                <Plus className="w-4 h-4 mr-1.5" /> New Role
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display">{editing ? `Edit Role · ${editing.name}` : "Create Role"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Role Name *</Label>
                    <Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="rounded-none mt-1" data-testid="role-name" disabled={isSuperAdminEditing} />
                  </div>
                  <div>
                    <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Description</Label>
                    <Input value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} className="rounded-none mt-1" data-testid="role-desc" />
                  </div>
                </div>

                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Module Permissions</Label>
                  <div className="border border-slate-200 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Module</th>
                          {actions.map(a => (
                            <th key={a} className="text-center px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{ACTION_LABELS[a]}</th>
                          ))}
                          <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">All / None</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modules.map((m) => {
                          const row = form.permissions[m] || EMPTY_PERM;
                          const allOn = actions.every(a => row[a]);
                          return (
                            <tr key={m} className="border-t border-slate-100">
                              <td className="px-3 py-2 font-medium text-slate-700">{MODULE_LABELS[m] || m}</td>
                              {actions.map(a => (
                                <td key={a} className="text-center px-3 py-2">
                                  <Checkbox
                                    checked={!!row[a]}
                                    onCheckedChange={() => togglePerm(m, a)}
                                    disabled={isSuperAdminEditing}
                                    data-testid={`perm-${m}-${a}`}
                                  />
                                </td>
                              ))}
                              <td className="text-right px-3 py-2">
                                <button
                                  onClick={() => toggleModule(m, !allOn)}
                                  disabled={isSuperAdminEditing}
                                  className="text-[11px] font-semibold uppercase tracking-wider text-[#002FA7] hover:underline disabled:text-slate-300"
                                >
                                  {allOn ? "Clear" : "Full"}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1">Ticking Create / Edit / Delete automatically enables View. Disabling View clears all actions.</div>
                </div>

                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">Retention Heads This Role Can Manage</Label>
                  <div className="border border-slate-200 p-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                    {heads.map((h) => (
                      <label key={h} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-2 py-1.5">
                        <Checkbox
                          checked={form.retention_heads.includes(h)}
                          onCheckedChange={() => toggleHead(h)}
                          disabled={isSuperAdminEditing}
                          data-testid={`head-${h}`}
                        />
                        <span>{h}</span>
                      </label>
                    ))}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1">Heads not checked here will be blocked from create/edit/pay/delete actions for users with this role.</div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-none">Cancel</Button>
                <Button onClick={submit} className="bg-[#002FA7] hover:bg-[#00227A] rounded-none" data-testid="submit-role">{editing ? "Update Role" : "Create Role"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-slate-200 px-4 py-3 mb-3 flex items-center flex-wrap gap-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Filters</div>
        <div className="flex items-center gap-2">
          <Label className="text-[11px] text-slate-600">Type</Label>
          <Select value={filterKind} onValueChange={setFilterKind}>
            <SelectTrigger className="rounded-none h-9 w-40" data-testid="roles-filter-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All roles</SelectItem>
              <SelectItem value="system">System only</SelectItem>
              <SelectItem value="custom">Custom only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-[11px] text-slate-600">Has Access To</Label>
          <Select value={filterModule} onValueChange={setFilterModule}>
            <SelectTrigger className="rounded-none h-9 w-52" data-testid="roles-filter-module"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Any module</SelectItem>
              {modules.map((m) => <SelectItem key={m} value={m}>{MODULE_LABELS[m] || m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-[11px] text-slate-600">Manages Head</Label>
          <Select value={filterHead} onValueChange={setFilterHead}>
            <SelectTrigger className="rounded-none h-9 w-56" data-testid="roles-filter-head"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Any retention head</SelectItem>
              {heads.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-slate-500">
            Showing <strong className="text-slate-900">{filteredRoles.length}</strong> of {roles.length}
          </span>
          {(activeFilterCount > 0 || q) && (
            <button
              onClick={clearAllFilters}
              className="text-[11px] font-semibold uppercase tracking-wider text-[#002FA7] hover:underline"
              data-testid="roles-filter-clear"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      <div className="card-surface overflow-x-auto rounded-sm">
        <table className="w-full text-sm" style={{ minWidth: 1100 }}>
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500 w-56">Role</th>
              <th className="text-left px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Modules &amp; Actions</th>
              <th className="text-left px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500 w-72">Retention Heads</th>
              <th className="text-right px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500 w-44">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRoles.length === 0 && (
              <tr><td colSpan={4} className="text-center text-sm text-slate-400 py-12">{(q || activeFilterCount > 0) ? "No roles match the current filters." : "No roles defined."}</td></tr>
            )}
            {pagedRoles.map((r) => {
              const rh = Array.isArray(r.retention_heads) && r.retention_heads.length ? r.retention_heads : heads;
              const allHeads = rh.length === heads.length;
              return (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/70 align-top">
                  <td className="px-4 py-4 w-56 align-top">
                    <div className="font-medium text-slate-900 flex items-center gap-1.5">
                      {r.is_system && <ShieldCheck className="w-3.5 h-3.5 text-[#002FA7] shrink-0" />}
                      <span className="truncate">{r.name}</span>
                    </div>
                    {r.description && (
                      <div className="text-[10px] text-slate-400 mt-1 break-words leading-snug">{r.description}</div>
                    )}
                  </td>
                  <td className="px-3 py-4 align-top">
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-6 gap-y-2">
                      {modules.map((m) => {
                        const row = normalizePerm(r.permissions?.[m]);
                        const tags = actions.filter(a => row[a]);
                        return (
                          <div key={m} className="text-[11px] flex items-start gap-2 min-w-0">
                            <span className="font-semibold text-slate-700 w-[110px] shrink-0 truncate">{MODULE_LABELS[m] || m}</span>
                            <div className="flex flex-wrap gap-1 min-w-0">
                              {tags.length === 0 ? (
                                <span className="text-slate-300">—</span>
                              ) : (
                                tags.map(a => (
                                  <span key={a} className={`px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${
                                    a === "view" ? "bg-slate-100 text-slate-700" :
                                    a === "create" ? "bg-emerald-50 text-emerald-700" :
                                    a === "edit" ? "bg-amber-50 text-amber-700" :
                                    "bg-red-50 text-red-700"
                                  }`}>{ACTION_LABELS[a]}</span>
                                ))
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-3 py-4 w-72 align-top">
                    {allHeads ? (
                      <span className="chip-soft info">All heads</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {rh.map(h => (
                          <span key={h} className="px-2 py-0.5 text-[10px] bg-indigo-50 text-indigo-700 rounded whitespace-nowrap">{h}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-4 text-right whitespace-nowrap w-44 align-top">
                    {canManage && (
                      <button onClick={() => startEdit(r)} className="text-xs font-semibold text-[#002FA7] hover:underline mr-3" data-testid={`edit-role-${r.name}`}>
                        <Edit3 className="w-3.5 h-3.5 inline mr-1" /> Edit
                      </button>
                    )}
                    {hasPerm("roles", "create") && (
                      <button onClick={() => clone(r)} className="text-xs font-semibold text-indigo-600 hover:text-indigo-900 mr-3" data-testid={`clone-role-${r.name}`} title="Clone role">
                        <Copy className="w-3.5 h-3.5 inline mr-1" /> Clone
                      </button>
                    )}
                    {!r.is_system && hasPerm("roles", "delete") && (
                      <button onClick={() => del(r.id)} className="text-red-500 hover:text-red-700" data-testid={`del-role-${r.name}`}>
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Pagination
          page={rolePage} setPage={setRolePage}
          pageSize={rolePageSize} setPageSize={setRolePageSize}
          totalPages={roleTotalPages} totalItems={roleTotalItems}
          testId="roles-pagination"
        />
      </div>
    </div>
  );
}
